/* eslint-disable camelcase */

export const FIELD_TYPES = [
  { value: 'TEXT_SHORT', label: 'Short Text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'DATE_YYYYMMDD', label: 'Date' },
  { value: 'DATETIME', label: 'Datetime' },
  { value: 'SELECT_SINGLE', label: 'Single Select' },
  { value: 'SELECT_MULTI', label: 'Multi Select' },
]

export const FIELD_TYPE_LABELS = {
  TEXT_SHORT: 'Short Text',
  NUMBER: 'Number',
  DATE_YYYYMMDD: 'Date',
  DATETIME: 'Datetime',
  SELECT_SINGLE: 'Single Select',
  SELECT_MULTI: 'Multi Select',
  DATE: 'Date',
  SELECT_ONE: 'Single Select',
  TEXT_LONG: 'Long Text',
  BOOLEAN: 'Boolean',
}

/**
 * Standard Org Custom Fields configured by backend
 */
export const STANDARD_CRM_SUBORG_CUSTOM_FIELDS = [
  {
    uiKey: 'locationAddress',
    label: 'Location Address',
    iType: 'TEXT_SHORT',
    order: '000',
    mandatory: 'NO',
    advanced: 'YES',
    isStandard: true,
  },
]

/**
 * Validates team title. Title must be between 2 and 100 characters.
 */
export function isValidTeamTitle(title) {
  if (typeof title !== 'string') return false
  const trimmed = title.trim()
  return trimmed.length >= 2 && trimmed.length <= 100
}

/**
 * Returns error string for team title or empty string if valid.
 */
export function getTeamTitleError(title) {
  if (!title || typeof title !== 'string' || !title.trim()) {
    return 'Title is required'
  }
  const trimmed = title.trim()
  if (trimmed.length < 2) {
    return 'Title must be at least 2 characters'
  }
  if (trimmed.length > 100) {
    return 'Title cannot exceed 100 characters'
  }
  return ''
}

/**
 * Automatically derives an UPPERCASE_SNAKE_CASE key from a label.
 */
export function deriveUiKey(label) {
  if (!label || typeof label !== 'string') return ''
  return label
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/**
 * Validates a UI key. Must be UPPERCASE_SNAKE_CASE alphanumeric.
 */
export function isValidUiKey(key) {
  if (!key || typeof key !== 'string') return false
  return /^[A-Z][A-Z0-9_]{1,63}$/.test(key)
}

/**
 * Determines whether a team record is the Main Org.
 */
export function isMainOrgTeam(team, parentOrgId) {
  if (!team) return false
  if (team.isMainOrg === true) return true
  if (parentOrgId && (team.id === parentOrgId || team.orgId === parentOrgId)) return true
  if (!team.parentOrgId && team.id) return true
  return false
}

/**
 * createdTs for a team. Prefer explicit createdTs; otherwise parse from id
 * which is `orgId-createdTs` for sub-org teams.
 */
export function getTeamCreatedTs(team) {
  if (!team || typeof team !== 'object') return 0
  if (team.createdTs != null) {
    const n = Number(team.createdTs)
    if (Number.isFinite(n)) return n
  }
  const id = String(team.id || team.orgId || '').trim()
  if (!id) return 0
  const parts = id.split('-')
  if (parts.length >= 2) {
    const suffix = Number(parts[parts.length - 1])
    if (Number.isFinite(suffix)) return suffix
  }
  const asNum = Number(id)
  return Number.isFinite(asNum) ? asNum : 0
}

/** Ascending by createdTs (oldest first); title as tiebreaker. */
export function compareTeamsByCreatedTs(a, b) {
  const diff = getTeamCreatedTs(a) - getTeamCreatedTs(b)
  if (diff !== 0) return diff
  return String((a && a.title) || '').localeCompare(String((b && b.title) || ''))
}

/**
 * Extracts custom fields from fieldDef (all fields except 'title').
 */
export function getCustomFields(fieldDef) {
  if (!fieldDef || !fieldDef.steps || !Array.isArray(fieldDef.steps)) return []
  const fields = []
  for (const step of fieldDef.steps) {
    if (Array.isArray(step.fields)) {
      for (const f of step.fields) {
        if (f.key !== 'title') {
          fields.push(f)
        }
      }
    }
  }
  return fields
}

/**
 * Extracts options for a specific field from any field definition.
 */
export function getFieldOptions(def, fieldKey) {
  if (!def || !def.steps || !Array.isArray(def.steps)) return []
  for (const step of def.steps) {
    if (Array.isArray(step.fields)) {
      const f = step.fields.find((field) => field.key === fieldKey)
      if (f && Array.isArray(f.options)) {
        return f.options.map((opt) => ({
          key: opt.key || opt.id || opt.value,
          label: opt.label || opt.title || opt.name || opt.key,
          text: opt.label || opt.title || opt.name || opt.key,
        }))
      }
    }
  }
  return []
}

/**
 * Finds a field object by key in a field definition.
 */
export function getFieldFromDef(def, fieldKey) {
  if (!def || !def.steps || !Array.isArray(def.steps)) return null
  for (const step of def.steps) {
    if (Array.isArray(step.fields)) {
      const f = step.fields.find((field) => field.key === fieldKey)
      if (f) return f
    }
  }
  return null
}

/**
 * Formats inbound routing information into summary chip descriptors.
 */
export function formatInboundRoutingSummary(team) {
  if (!team) return []
  const chips = []

  // Page Slugs
  const slugs = Array.isArray(team.inboundPageSlugs) ? team.inboundPageSlugs : []
  if (slugs.length > 0) {
    chips.push({
      type: 'slugs',
      label: `${slugs.length} ${slugs.length === 1 ? 'Slug' : 'Slugs'}`,
      tooltip: `Website Slugs: ${slugs.join(', ')}`,
      count: slugs.length,
    })
  }

  // Meta Forms
  const metaForms = Array.isArray(team.inboundMetaFormIds) ? team.inboundMetaFormIds : []
  if (metaForms.length > 0) {
    chips.push({
      type: 'metaForms',
      label: `${metaForms.length} Meta Form${metaForms.length === 1 ? '' : 's'}`,
      tooltip: `Meta Form IDs: ${metaForms.join(', ')}`,
      count: metaForms.length,
    })
  }

  // Phone numbers (Calls and WhatsApp)
  const calls = Array.isArray(team.inboundCallNumbers) ? team.inboundCallNumbers : []
  const was = Array.isArray(team.inboundWANumbers) ? team.inboundWANumbers : []

  if (calls.length > 0 && was.length > 0) {
    chips.push({
      type: 'numbers',
      label: `${calls.length} Call, ${was.length} WA`,
      tooltip: `Inbound Call: ${calls.join(', ')} | Inbound WA: ${was.join(', ')}`,
      count: calls.length + was.length,
    })
  } else if (calls.length > 0) {
    chips.push({
      type: 'numbers',
      label: `${calls.length} ${calls.length === 1 ? 'Call' : 'Calls'}`,
      tooltip: `Inbound Call: ${calls.join(', ')}`,
      count: calls.length,
    })
  } else if (was.length > 0) {
    chips.push({
      type: 'numbers',
      label: `${was.length} WA`,
      tooltip: `Inbound WA: ${was.join(', ')}`,
      count: was.length,
    })
  }

  return chips
}

/**
 * Formats outbound channels information into summary chips.
 */
export function formatOutboundSummary(team, fieldDefOutbound) {
  if (!team) return []
  const chips = []

  const outgoingWaOptions = getFieldOptions(fieldDefOutbound, 'outgoingWaNumberId')
  const outgoingCtcOptions = getFieldOptions(fieldDefOutbound, 'outgoingCtcNumberId')

  if (team.outgoingWaNumberId) {
    const opt = outgoingWaOptions.find((o) => o.key === team.outgoingWaNumberId)
    const label = opt ? opt.label : team.outgoingWaNumberId
    chips.push({
      type: 'wa',
      label: `WA: ${label}`,
      tooltip: `Outgoing WhatsApp: ${label}`,
      value: team.outgoingWaNumberId,
    })
  }

  if (team.outgoingCtcNumberId) {
    const opt = outgoingCtcOptions.find((o) => o.key === team.outgoingCtcNumberId)
    const label = opt ? opt.label : team.outgoingCtcNumberId
    chips.push({
      type: 'ctc',
      label: `CTC: ${label}`,
      tooltip: `Outgoing Click-to-Call: ${label}`,
      value: team.outgoingCtcNumberId,
    })
  }

  return chips
}

/**
 * Extracts custom variable values from team item.
 */
export function formatCustomVariablesSummary(team, fieldDef) {
  if (!team) return []
  const customFields = getCustomFields(fieldDef)
  const summaries = []

  for (const field of customFields) {
    let val = team[field.key]
    if (val == null && team.customFieldValues && typeof team.customFieldValues === 'object') {
      val = team.customFieldValues[field.key]
    }
    if (val != null && String(val).trim() !== '') {
      summaries.push({
        key: field.key,
        label: field.label || field.key,
        value: String(val),
      })
    }
  }

  return summaries
}

/**
 * Retrieves the location address from a team record if present.
 */
export function getTeamLocationAddress(team) {
  if (!team) return ''
  const val =
    team.locationAddress ||
    team.location_address ||
    team.LOCATION_ADDRESS ||
    (team.customFieldValues && (
      team.customFieldValues.locationAddress ||
      team.customFieldValues.location_address ||
      team.customFieldValues.LOCATION_ADDRESS
    ))
  if (val != null && String(val).trim() !== '') {
    return String(val).trim()
  }
  return ''
}

/**
 * Extracts custom field values dictionary for form submissions.
 */
export function extractCustomFieldValues(formData, fieldDef) {
  const customFields = getCustomFields(fieldDef)
  const values = {}
  for (const field of customFields) {
    if (formData[field.key] !== undefined && formData[field.key] !== null && formData[field.key] !== '') {
      values[field.key] = formData[field.key]
    }
  }
  for (const std of STANDARD_CRM_SUBORG_CUSTOM_FIELDS) {
    if (formData[std.uiKey] !== undefined && formData[std.uiKey] !== null && formData[std.uiKey] !== '') {
      values[std.uiKey] = formData[std.uiKey]
    }
  }
  return values
}

/**
 * Route generator helpers
 */
export function getTeamsRoute(orgId) {
  return `/admin/orgs/${orgId}/teams`
}

export function getTeamsFieldsRoute(orgId) {
  return `/admin/orgs/${orgId}/teams-fields`
}

export function getTeamStaffRoute(orgId) {
  return `/admin/orgs/${orgId}/staff`
}

/**
 * Normalizes field type for DynamicForm
 */
export function normalizeFieldType(type) {
  const t = String(type || '').toLowerCase()
  if (t === 'select_multi' || t === 'check_box') return 'check_box'
  if (t === 'select' || t === 'select_single' || t === 'select_one' || t === 'radio') return 'select'
  if (t === 'date_yyyymmdd') return 'date_yyyymmdd'
  if (t === 'datetime') return 'dateTime'
  if (t === 'date') return 'date'
  return 'edit_text'
}

/**
 * Builds DynamicForm definition for creating a new team
 * Only takes the team name/title; all routing, outbound, and custom fields are configured later.
 */
export function buildCreateTeamFormDef() {
  const fields = [
    {
      key: 'title',
      label: 'Team Name',
      type: 'edit_text',
      placement: 'left',
      v_required: { value: true, err: 'Team title is required (2-100 characters)' },
    },
  ]

  return {
    title: 'New Team',
    formType: 'add',
    _dialogType: 'create',
    ctaButtonText: 'Create Team',
    maxWidth: 'md',
    steps: [{ fields }],
  }
}

/**
 * Builds DynamicForm definition for editing team details and custom fields
 */
export function buildEditTeamFormDef({ team, isMain, fieldDef }) {
  const customFields = getCustomFields(fieldDef)
  const mappedCustomFields = customFields.map((f) => {
    const standardField = STANDARD_CRM_SUBORG_CUSTOM_FIELDS.find(
      (s) => s.uiKey === f.key || (f.key && s.uiKey.toLowerCase() === f.key.toLowerCase())
    )
    const isAdvanced = Boolean(
      f.advanced === true ||
      f.advanced === 'YES' ||
      (standardField && (standardField.advanced === 'YES' || standardField.advanced === true))
    )

    return {
      key: f.key,
      label: f.label || (standardField && standardField.label) || f.key,
      type: normalizeFieldType(f.type || f.iType || (standardField && standardField.iType)),
      placement: 'left',
      advanced: isAdvanced,
      options: f.options,
      v_required: f.v_required || { value: false, err: '' },
    }
  })

  // Ensure standard custom fields are present in the form if not returned in fieldDef
  STANDARD_CRM_SUBORG_CUSTOM_FIELDS.forEach((std) => {
    const exists = mappedCustomFields.some(
      (f) => f.key === std.uiKey || (f.key && f.key.toLowerCase() === std.uiKey.toLowerCase())
    )
    if (!exists) {
      mappedCustomFields.push({
        key: std.uiKey,
        label: std.label,
        type: normalizeFieldType(std.iType),
        placement: 'left',
        advanced: Boolean(std.advanced === 'YES' || std.advanced === true),
        v_required: { value: false, err: '' },
      })
    }
  })

  const fields = [
    {
      key: 'title',
      label: 'Team Name',
      type: 'edit_text',
      placement: 'left',
      disabled: isMain,
      v_required: isMain ? { value: false, err: '' } : { value: true, err: 'Team title is required (2-100 characters)' },
    },
    ...mappedCustomFields,
  ]

  return {
    title: isMain ? `Edit Custom Fields — ${team.title}` : `Edit Details — ${team.title}`,
    formType: 'edit',
    _dialogType: 'edit',
    _selectedTeam: team,
    ctaButtonText: 'Save Changes',
    ...(isMain ? { infoLabel: 'Configure organization default fallback values for custom variables.' } : {}),
    maxWidth: 'md',
    steps: [{ fields }],
  }
}

/**
 * Builds DynamicForm definition for editing inbound routing
 */
export function buildRoutingFormDef({ team, fieldDefRouting }) {
  const existingSlugs = (team.inboundPageSlugs || []).map((s) => ({ key: s, label: s, text: s }))
  const existingMeta = (team.inboundMetaFormIds || []).map((s) => ({ key: s, label: s, text: s }))

  const fields = [
    {
      key: 'inboundPageSlugs',
      label: 'Inbound Website Page Slugs',
      type: 'check_box',
      addOptions: true,
      placement: 'left',
      options: existingSlugs,
      tip: 'Case-insensitive substring match against website form page slugs (e.g. "siliguri"). Hit Enter or Add to add.',
      help: 'Case-insensitive substring match against website form page slugs (e.g. "siliguri"). Hit Enter or Add to add.',
      v_required: { value: false, err: '' },
    },
    {
      key: 'inboundMetaFormIds',
      label: 'Inbound Meta Form IDs',
      type: 'check_box',
      addOptions: true,
      placement: 'left',
      options: existingMeta,
      tip: 'Exact match against Meta Instant Form IDs. Hit Enter or Add to add.',
      help: 'Exact match against Meta Instant Form IDs. Hit Enter or Add to add.',
      v_required: { value: false, err: '' },
    },
    {
      key: 'inboundCallNumbers',
      label: 'Inbound Call CRM Numbers',
      type: 'check_box',
      placement: 'left',
      options: getFieldOptions(fieldDefRouting, 'inboundCallNumbers'),
      v_required: { value: false, err: '' },
    },
    {
      key: 'inboundWANumbers',
      label: 'Inbound WhatsApp CRM Numbers',
      type: 'check_box',
      placement: 'left',
      options: getFieldOptions(fieldDefRouting, 'inboundWANumbers'),
      v_required: { value: false, err: '' },
    },
  ]

  return {
    title: `Inbound Routing — ${team.title}`,
    formType: 'edit',
    _dialogType: 'routing',
    _selectedTeam: team,
    ctaButtonText: 'Save Routing',
    infoLabel: 'When an unassigned lead comes in via a matching Web Form URL slug, Meta Form ID, or incoming Call/WhatsApp number, it will automatically be assigned to this team.',
    maxWidth: 'md',
    steps: [{ fields }],
  }
}

/**
 * Builds DynamicForm definition for editing outbound channels
 */
export function buildOutboundFormDef({ team, fieldDefOutbound }) {
  const fields = [
    {
      key: 'outgoingWaNumberId',
      label: 'Outgoing WhatsApp Number',
      type: 'select',
      placement: 'left',
      options: getFieldOptions(fieldDefOutbound, 'outgoingWaNumberId'),
      v_required: { value: false, err: '' },
    },
    {
      key: 'outgoingCtcNumberId',
      label: 'Outgoing Click-to-Call Virtual Number',
      type: 'select',
      placement: 'left',
      options: getFieldOptions(fieldDefOutbound, 'outgoingCtcNumberId'),
      v_required: { value: false, err: '' },
    },
  ]

  return {
    title: `Outbound Configuration — ${team.title}`,
    formType: 'edit',
    _dialogType: 'outbound',
    _selectedTeam: team,
    ctaButtonText: 'Save Configuration',
    maxWidth: 'md',
    steps: [{ fields }],
  }
}

/**
 * Builds DynamicForm definition for adding an organization custom field
 */
export function buildAddOrgFieldFormDef() {
  const typeOptions = FIELD_TYPES.map((t) => ({ key: t.value, label: t.label, text: t.label }))

  return {
    title: 'Add Org Custom Field',
    formType: 'add',
    _dialogType: 'addField',
    ctaButtonText: 'Add Field',
    maxWidth: 'md',
    steps: [
      {
        fields: [
          {
            key: 'label',
            label: 'Field Label',
            type: 'edit_text',
            placement: 'left',
            v_required: { value: true, err: 'Field label is required' },
          },
          {
            key: 'uiKey',
            label: 'Variable Key',
            type: 'edit_text',
            placement: 'left',
            tip: 'Uppercase identifier (e.g. SUPPORT_PHONE). Leave empty to derive from label.',
            help: 'Uppercase identifier (e.g. SUPPORT_PHONE). Leave empty to derive from label.',
            v_required: { value: false, err: '' },
          },
          {
            key: 'iType',
            label: 'Field Type',
            type: 'select',
            placement: 'left',
            defaultValue: 'TEXT_SHORT',
            options: typeOptions,
            v_required: { value: true, err: 'Field type is required' },
          },
          {
            key: 'options',
            label: 'Allowed Options (for Select types)',
            type: 'check_box',
            addOptions: true,
            placement: 'left',
            options: [],
            tip: 'Type an option and hit Enter or click Add to add.',
            help: 'Type an option and hit Enter or click Add to add.',
            condition: {
              key: 'iType',
              op: 'is',
              values: ['SELECT_SINGLE', 'SELECT_MULTI', 'SELECT_ONE'],
            },
            v_required: { value: false, err: '' },
          },
        ],
      },
    ],
  }
}

/**
 * Builds DynamicForm definition for editing an organization custom field
 */
export function buildEditOrgFieldFormDef({ field }) {
  const typeOptions = FIELD_TYPES.map((t) => ({ key: t.value, label: t.label, text: t.label }))
  const existingOptions = (Array.isArray(field.options) ? field.options : []).map((opt) => {
    const val = typeof opt === 'object' ? opt.key || opt.value || opt.label : opt
    return { key: val, label: val, text: val }
  })

  return {
    title: `Edit Field — ${field.label || field.uiKey}`,
    formType: 'edit',
    _dialogType: 'editField',
    _editingField: field,
    ctaButtonText: 'Save Changes',
    maxWidth: 'md',
    steps: [
      {
        fields: [
          {
            key: 'label',
            label: 'Field Label',
            type: 'edit_text',
            placement: 'left',
            v_required: { value: true, err: 'Field label is required' },
          },
          {
            key: 'uiKey',
            label: 'Variable Key',
            type: 'edit_text',
            placement: 'left',
            disabled: true,
            tip: 'Variable key cannot be changed after creation.',
            help: 'Variable key cannot be changed after creation.',
            v_required: { value: false, err: '' },
          },
          {
            key: 'iType',
            label: 'Field Type',
            type: 'select',
            placement: 'left',
            disabled: true,
            options: typeOptions,
            v_required: { value: false, err: '' },
          },
          {
            key: 'options',
            label: 'Allowed Options',
            type: 'check_box',
            addOptions: true,
            placement: 'left',
            options: existingOptions,
            tip: 'Type an option and hit Enter or click Add to add.',
            help: 'Type an option and hit Enter or click Add to add.',
            condition: {
              key: 'iType',
              op: 'is',
              values: ['SELECT_SINGLE', 'SELECT_MULTI', 'SELECT_ONE'],
            },
            v_required: { value: false, err: '' },
          },
        ],
      },
    ],
  }
}
